import { useState, useRef, useCallback } from "react";
import { synthesizeSpeech } from "../api";

/**
 * useTTS — Text-to-Speech hook for scenario dialog playback.
 *
 * Returns:
 *   playingTurnId  — id of the turn currently playing (null if idle)
 *   loadingTurnId  — id of the turn whose audio is being fetched (null if none)
 *   playTurn(turn) — play a single ScenarioTurn
 *   playAll(turns) — play all turns sequentially with auto-scroll
 *   stop()         — cancel any active playback
 *   turnRefsRef    — ref object; assign turnRefsRef.current[turn.id] = el per turn
 */
export function useTTS() {
  const [playingTurnId, setPlayingTurnId] = useState(null);
  const [loadingTurnId, setLoadingTurnId] = useState(null);

  const audioRef = useRef(null);
  const cancelRef = useRef(false);
  // Populated by TopicDetail: { [turn.id]: DOM element }
  const turnRefsRef = useRef({});

  const stop = useCallback(() => {
    cancelRef.current = true;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingTurnId(null);
    setLoadingTurnId(null);
  }, []);

  /** Play one turn and resolve when audio ends (or on error/cancel). */
  const _playOneTurn = useCallback(async (turn) => {
    setLoadingTurnId(turn.id);
    setPlayingTurnId(null);

    const data = await synthesizeSpeech(turn.arabic, turn.speaker);
    if (cancelRef.current) return;

    const audio = new Audio(`data:audio/mp3;base64,${data.audio_content}`);
    audioRef.current = audio;
    setLoadingTurnId(null);
    setPlayingTurnId(turn.id);

    await new Promise((resolve) => {
      audio.onended = resolve;
      audio.onerror = resolve;
      audio.play().catch(resolve);
    });

    if (!cancelRef.current) setPlayingTurnId(null);
  }, []);

  const playTurn = useCallback(
    async (turn) => {
      stop();
      cancelRef.current = false;
      try {
        await _playOneTurn(turn);
      } catch {
        setLoadingTurnId(null);
        setPlayingTurnId(null);
      }
    },
    [stop, _playOneTurn]
  );

  const playAll = useCallback(
    async (turns, { onComplete, autoScroll = true } = {}) => {
      stop();
      cancelRef.current = false;

      let completed = false;
      try {
        for (const turn of turns) {
          if (cancelRef.current) break;

          // Scroll the upcoming turn into view. When autoScroll is true we use
          // scrollIntoView (page-level, original behaviour). When false, we
          // scroll only the nearest scrollable ancestor (the turn's own
          // container) so the page itself doesn't move.
          const el = turnRefsRef.current[turn.id];
          if (el) {
            if (autoScroll) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
              // find nearest scrollable ancestor
              let p = el.parentElement;
              while (p) {
                const oy = getComputedStyle(p).overflowY;
                if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) break;
                p = p.parentElement;
              }
              if (p) {
                // Robust offset regardless of positioning context.
                const er = el.getBoundingClientRect();
                const pr = p.getBoundingClientRect();
                const top = p.scrollTop + (er.top - pr.top) - p.clientHeight / 2 + er.height / 2;
                p.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
              }
            }
          }

          try {
            await _playOneTurn(turn);
          } catch {
            setLoadingTurnId(null);
            setPlayingTurnId(null);
            return;
          }

          if (cancelRef.current) break;
        }

        if (!cancelRef.current) {
          setPlayingTurnId(null);
          setLoadingTurnId(null);
          completed = turns.length > 0;
        }
      } finally {
        // Fire only on a genuine full-sequence playback finish (no user-stop).
        if (completed && typeof onComplete === "function") {
          try {
            onComplete();
          } catch {
            /* swallow callback errors */
          }
        }
      }
    },
    [stop, _playOneTurn]
  );

  return { playingTurnId, loadingTurnId, playTurn, playAll, stop, turnRefsRef };
}
